import { error as logError } from '@pressreader/logger';
import { httpGet, httpPost, withIdempotencyKey } from '@pressreader/services';

import {
    AnyPurchaseProductDto,
    AvailableProductsDto,
    IAvailableProductsRequestDto,
    IBasketDto,
    ICheckoutResponseDto,
    IGiftCardProductDto,
    IGiftProductCheckoutRequestDto,
} from './types';

const serviceBasePath = 'v1/purchase';

const POLLING_INTERVAL_MS = 1000;
const POLLING_TIMEOUT_MS = 5 * 60 * 1000;

function getAvailableProducts(request: IAvailableProductsRequestDto) {
    return httpGet<AnyPurchaseProductDto[]>(serviceBasePath, 'products', request);
}

// Products for a specific promo campaign, each with a `token` for V1 checkout.
// An empty list means the user is not eligible for the campaign.
function getPromoProducts(promoCampaign: string): Promise<AnyPurchaseProductDto[]> {
    return httpGet<AnyPurchaseProductDto[]>(serviceBasePath, `promo/${encodeURIComponent(promoCampaign)}/products`);
}

function getAllAvailableProducts(issueKey?: string): Promise<AvailableProductsDto> {
    return httpGet('v1/products', issueKey ? { issueKey } : undefined);
}

function getAvailableGifts(request: IAvailableProductsRequestDto) {
    return httpGet<IGiftCardProductDto[]>(serviceBasePath, 'gifts', request);
}

function getBasket(productToken: string, billingCountry?: string): Promise<IBasketDto> {
    return httpGet<IBasketDto>(serviceBasePath, 'basket', { productToken, billingCountry });
}

function checkoutGift(request: IGiftProductCheckoutRequestDto, idempotencyKey?: string): Promise<ICheckoutResponseDto> {
    return httpPost(
        serviceBasePath,
        'checkout/gift',
        JSON.stringify(request),
        withIdempotencyKey({ contentType: 'application/json', disableRetry: true }, idempotencyKey),
    );
}

function checkout(productToken: string, idempotencyKey?: string): Promise<ICheckoutResponseDto> {
    return httpPost(
        serviceBasePath,
        'checkout',
        JSON.stringify({ productToken }),
        withIdempotencyKey({ contentType: 'application/json', disableRetry: true }, idempotencyKey),
    );
}

function checkPendingOrder(orderId: string): Promise<ICheckoutResponseDto> {
    return httpGet(serviceBasePath, `pending-order/${orderId}`);
}

async function awaitPendingOrder(orderId: string): Promise<ICheckoutResponseDto> {
    const deadline = Date.now() + POLLING_TIMEOUT_MS;
    let hadError = false;
    while (Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, POLLING_INTERVAL_MS));
        let response: ICheckoutResponseDto;
        try {
            response = await checkPendingOrder(orderId);
        } catch (ex) {
            // Transient errors (network blip, 5xx) must not kick the user
            // out of the 3DS verification flow. Log and keep polling until
            // the deadline; on timeout we report whether errors occurred so
            // the failure can be correlated with logs.
            logError(ex);
            hadError = true;
            continue;
        }
        if (response.status !== 'Pending') {
            return response;
        }
        hadError = false;
    }
    if (hadError) {
        throw new Error(`Pending order ${orderId} polling timed out after ${POLLING_TIMEOUT_MS}ms (see logs for last error)`);
    }
    throw new Error(`Pending order ${orderId} did not resolve within ${POLLING_TIMEOUT_MS}ms`);
}

export {
    awaitPendingOrder,
    checkout,
    checkoutGift,
    checkPendingOrder,
    getAllAvailableProducts,
    getAvailableGifts,
    getAvailableProducts,
    getBasket,
    getPromoProducts,
};
